import { useRef, useState } from 'react';

import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { useClickOutside } from '@src/hooks/useClickOutside';

import NavButton from './NavButton';
import NavItemIcon from './NavItemIcon';

// Flyout Menu Component
export function NavListMenu({listMenu, listMenuName, currentPage, onNavigateToTop}) {
  const [isOpen, setIsOpen] = useState(false);
  const flyoutRef = useRef(null);

  const active = currentPage[0]?.toLowerCase() === listMenuName?.toLowerCase();

  // Close the menu if a click/focus happens outside of the component
  useClickOutside(flyoutRef, () => setIsOpen(false));

  // Calculate grid columns based on menu size
  const isLargeMenu = listMenu.length > 4;
  const isCompareMenu = listMenuName === 'Compare';

  // Tailwind's utility for conditional classes based on state
  const menuClasses = `
        origin-top-right absolute top-full left-1/2 -translate-x-1/2
        ${isCompareMenu ? 'w-[780px]' : isLargeMenu ? 'w-[680px]' : 'min-w-[320px]'} bg-white border border-slate-200 p-5 rounded-xl
        shadow-xl transition ease-out duration-200
        ${isOpen
            ? 'opacity-100 translate-y-0 transform pointer-events-auto'
            : 'opacity-0 -translate-y-2 transform pointer-events-none'
        }
    `;

  return (
    <div
      ref={flyoutRef} // Attach ref for click/focus outside detection
      className="p-4 lg:px-1 relative flex items-center space-x-1"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}

    >
      <NavButton
        active={currentPage.length === 0}
        sx={(theme) => ({
          textTransform: 'none',
          fontWeight: active ? 600 : 500,
          color: active ? theme.palette.primary.main : theme.palette.text.secondary,

          '&:hover': {
            bgcolor: theme.palette.action.hover,
            color: active ? theme.palette.primary.main : theme.palette.text.primary,
          },
        })}
      >
        {listMenuName}
      </NavButton>

      <button
        aria-expanded={isOpen}
        className="shrink-0 p-1"
        onClick={(e) => {
          e.preventDefault();
          setIsOpen(!isOpen);
        }}
      >
        <svg width="12" height="12" className="w-5 h-3 fill-slate-500" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 2.586 11.414 4 6 9.414.586 4 2 2.586l4 4z" />
        </svg>
      </button>

      {/* 2nd level menu */}
      <ul
        className={menuClasses}
        style={{
          visibility: isOpen ? 'visible' : 'hidden',
          display: isLargeMenu ? 'grid' : 'block',
          gridTemplateColumns: isLargeMenu ? 'repeat(2, 1fr)' : undefined,
          gap: '12px',
        }}
      >
        {listMenu.map((item) => {
          const isActive = (currentPage && currentPage[1]) === item.href.split('/')[2];

          return (
            <li key={item.name} style={{ listStyle: 'none' }}>
              <NavButton
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  flexDirection: 'row',
                  gap: '16px',
                  px: 2.5,
                  py: 2,
                  width: '100%',
                  minHeight: '64px',
                  borderRadius: 2,
                  transition: 'all 0.2s',
                  '&.Mui-focusVisible': {
                    backgroundColor: 'transparent',
                  },
                  '&.Mui-focusVisible:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                  },
                  backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.06) : 'inherit',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.06),
                    '& .MuiTypography-subtitle2': {
                      color: theme.palette.primary.main,
                    },
                  },
                })}
                onClick={() => onNavigateToTop(item.href)}
              >
                <NavItemIcon icon={item.icon} name={item.name} />

                {/* Text Content */}
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                  }}
                >
                  <Typography
                    fontWeight={600}
                    variant="subtitle2"
                    sx={{
                      display: 'block',
                      textAlign: 'left',
                      lineHeight: 1.3,
                      fontSize: '0.9375rem',
                      marginBottom: '2px',
                    }}
                  >
                    {item.name}
                  </Typography>

                  {item.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        textAlign: 'left',
                        fontSize: '0.8125rem',
                        lineHeight: 1.4,
                        whiteSpace: 'normal',
                        overflow: 'hidden',
                        maxWidth: '100%',
                      }}
                    >
                      {item.description}
                    </Typography>
                  )}
                </div>
              </NavButton>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default NavListMenu;
